import { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  GET_SSI_CONTEXT_QUERY,
} from '../graphql/queries';
import { SiteStudyContextType, StudySiteInvestigator } from '../types/siteStudy';

const UNINITIALIZED = Symbol('SiteStudyContext.uninitialized');
const SiteStudyContext = createContext<SiteStudyContextType | typeof UNINITIALIZED>(UNINITIALIZED);

export const SiteStudyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { ssiId } = useParams<{ ssiId: string }>();

  // Single query to load the SSI with denormalized study/site/sponsor context
  const { data: ssiData, loading: ssiLoading, error: ssiError } = useQuery(GET_SSI_CONTEXT_QUERY, {
    variables: { id: ssiId },
    skip: !ssiId,
  });

  const ssi = ssiData?.studySiteInvestigator;

  const value = useMemo<SiteStudyContextType>(() => {
    const basePath = ssiId ? `/ssi/${ssiId}` : '/studies';

    if (!ssiId || !ssi) {
      // Return a loading-state value so useSiteStudy() never sees null
      return {
        studyId: '',
        siteOrganizationId: '',
        studySiteInvestigatorId: '',
        study: null,
        siteOrganization: null,
        primaryAlias: null,
        principalInvestigator: null,
        investigators: [],
        selectedInvestigatorId: null,
        sponsorName: null,
        loading: true,
        error: ssiError?.message || null,
        basePath,
        buildPath: (subpath: string) => `${basePath}/${subpath}`,
      };
    }

    // Build single-element investigators array from the current SSI
    const investigators: StudySiteInvestigator[] = (ssi.physician || ssi.sourcePiName)
      ? [{
          id: ssiId,
          studyId: ssi.studyId,
          siteOrganizationId: ssi.siteOrganizationId,
          physicianId: ssi.physicianId || '',
          status: ssi.status || 'ACTIVE',
          sourcePiName: ssi.sourcePiName || null,
          physician: ssi.physician || null,
          createdAt: ssi.createdAt || '',
        }]
      : [];

    return {
      studyId: ssi.studyId,
      siteOrganizationId: ssi.siteOrganizationId,
      studySiteInvestigatorId: ssiId,
      study: {
        id: ssi.studyId,
        protocolId: ssi.studyProtocolId || '',
        name: ssi.studyName || '',
        status: null,
        protocolAliases: [],
      },
      siteOrganization: {
        id: ssi.siteOrganizationId,
        name: ssi.siteOrganizationName || '',
        domain: null,
        principalInvestigator: null,
        ctmsSystem: null,
        paymentSystems: null,
        senderAddress: null,
        refreshCadence: null,
        lastCtmsPull: null,
        lastVivaPull: null,
        lastGpsPull: null,
        ctaEffectiveDate: null,
        firstPatientVisitDate: null,
        screeningCount: null,
        randomizedCount: null,
        screenFailedCount: null,
      },
      primaryAlias: ssi.primaryProtocolAlias || null,
      principalInvestigator: ssi.physician
        ? `${ssi.physician.firstName} ${ssi.physician.lastName}`
        : ssi.sourcePiName || null,
      investigators,
      selectedInvestigatorId: ssiId,
      sponsorName: ssi.sponsorName || null,
      loading: ssiLoading,
      error: ssiError?.message || null,
      basePath,
      buildPath: (subpath: string) => `${basePath}/${subpath}`,
    };
  }, [ssiId, ssi, ssiLoading, ssiError]);

  return <SiteStudyContext.Provider value={value}>{children}</SiteStudyContext.Provider>;
};

export const useSiteStudy = (): SiteStudyContextType => {
  const context = useContext(SiteStudyContext);
  if (context === UNINITIALIZED) {
    throw new Error('useSiteStudy must be used within a SiteStudyProvider');
  }
  return context;
};

export const useSiteStudyOptional = (): SiteStudyContextType | null => {
  const context = useContext(SiteStudyContext);
  if (context === UNINITIALIZED) return null;
  return context;
};

export default SiteStudyContext;
